import * as React from "react"
import { createPortal } from "react-dom"
import { XIcon, ChevronRight, ChevronLeft, PartyPopper, type LucideIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { scrollTourTargetIntoView } from "@/lib/tours/scrollTourTargetIntoView"
import {
    findTourTargetElement,
    isTourStepTargetReady,
} from "@/lib/tours/tourTargetReady"
import {
    buildTourSpotlightMaskImage,
    expandTourSpotlightRect,
} from "@/lib/tours/tourSpotlightMask"
import {
    TOUR_TOOLTIP_DEFAULT_HEIGHT,
    TOUR_TOOLTIP_DEFAULT_WIDTH,
    computeTourTooltipPosition,
} from "@/lib/tours/tourTooltipPosition"
import { Button } from "./Button"
import {
    Card,
    CardContent,
    CardFooter,
    CardHeader,
    CardTitle,
} from "./Card"

export interface TourStep {
    content: React.ReactNode
    selectorId: string
    position?: "top" | "bottom" | "left" | "right"
    /** Optional hook when this step becomes active (e.g. switch tabs before highlighting). */
    onStepEnter?: () => void
    /** How long to poll for a step target after onStepEnter (e.g. drawer enter animation). */
    targetPollTimeoutMs?: number
    /** Wait until a drawer panel finishes its enter animation before highlighting. */
    waitForPanelReady?: boolean
}

/** @deprecated Use TourStep instead */
export type Step = TourStep

interface TourContextValue {
    steps: TourStep[]
    setSteps: (steps: TourStep[]) => void
    isTourCompleted: boolean
    setIsTourCompleted: (completed: boolean) => void
    currentStepIndex: number
    isActive: boolean
    /** Atomically load steps and open the overlay (avoids stale step state from setSteps + start). */
    startWithSteps: (steps: TourStep[]) => void
    start: () => void
    close: () => void
    next: () => void
    previous: () => void
}

const TourContext = React.createContext<TourContextValue | null>(null)

export function useTour(): TourContextValue {
    const context = React.useContext(TourContext)
    if (!context) {
        throw new Error("useTour must be used within a TourProvider")
    }
    return context
}

export type TourCompleteReason = 'finished' | 'dismissed'

interface TourProviderProps {
    children: React.ReactNode
    isTourCompleted?: boolean
    onComplete?: (reason: TourCompleteReason) => void
}

export function TourProvider({ children, isTourCompleted: initialCompleted = false, onComplete }: TourProviderProps) {
    const [steps, setSteps] = React.useState<TourStep[]>([])
    const [isTourCompleted, setIsTourCompleted] = React.useState(initialCompleted)
    const [currentStepIndex, setCurrentStepIndex] = React.useState(0)
    const [isActive, setIsActive] = React.useState(false)

    // User/preferences often load after first paint; keep local state aligned with persisted completion.
    React.useEffect(() => {
        if (initialCompleted) {
            setIsTourCompleted(true)
        }
    }, [initialCompleted])

    const start = React.useCallback(() => {
        if (steps.length > 0) {
            setCurrentStepIndex(0)
            setIsActive(true)
        }
    }, [steps])
    const startWithSteps = React.useCallback((newSteps: TourStep[]) => {
        if (newSteps.length === 0) return
        setSteps(newSteps)
        setCurrentStepIndex(0)
        setIsActive(true)
    }, [])


    const close = React.useCallback(() => {
        setIsActive(false)
        setCurrentStepIndex(0)
        setIsTourCompleted(true)
        onComplete?.('dismissed')
    }, [onComplete])

    const next = React.useCallback(() => {
        if (currentStepIndex < steps.length - 1) {
            setCurrentStepIndex((prev) => prev + 1)
        } else {
            setIsActive(false)
            setCurrentStepIndex(0)
            setIsTourCompleted(true)
            onComplete?.('finished')
        }
    }, [currentStepIndex, steps.length, onComplete])

    const previous = React.useCallback(() => {
        if (currentStepIndex > 0) {
            setCurrentStepIndex((prev) => prev - 1)
        }
    }, [currentStepIndex])

    const value: TourContextValue = {
        steps,
        setSteps,
        isTourCompleted,
        setIsTourCompleted,
        currentStepIndex,
        isActive,
        startWithSteps,
        start,
        close,
        next,
        previous,
    }

    return (
        <TourContext.Provider value={value}>
            {children}
            {isActive && steps.length > 0 && (
                <TourOverlay
                    step={steps[currentStepIndex]}
                    stepIndex={currentStepIndex}
                    totalSteps={steps.length}
                    onNext={next}
                    onPrevious={previous}
                    onClose={close}
                />
            )}
        </TourContext.Provider>
    )
}

interface TourOverlayProps {
    step: TourStep
    stepIndex: number
    totalSteps: number
    onNext: () => void
    onPrevious: () => void
    onClose: () => void
}

type TourTargetRectState = {
    top: number
    left: number
    width: number
    height: number
    right: number
}

function rectToTargetState(rect: ReturnType<Element["getBoundingClientRect"]>): TourTargetRectState {
    return {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        right: rect.right,
    }
}

function tourRectsEqual(
    a: TourTargetRectState | null,
    b: TourTargetRectState | null,
): boolean {
    if (a === b) return true
    if (!a || !b) return false
    return (
        Math.abs(a.top - b.top) < 0.5 &&
        Math.abs(a.left - b.left) < 0.5 &&
        Math.abs(a.width - b.width) < 0.5 &&
        Math.abs(a.height - b.height) < 0.5
    )
}

function tourPositionsEqual(
    a: { top: number; left: number },
    b: { top: number; left: number },
): boolean {
    return Math.abs(a.top - b.top) < 0.5 && Math.abs(a.left - b.left) < 0.5
}

function TourOverlay({ step, stepIndex, totalSteps, onNext, onPrevious, onClose }: TourOverlayProps) {
    const tooltipRef = React.useRef<HTMLDivElement>(null)
    const hasLockedTargetRef = React.useRef(false)
    const hasScrolledRef = React.useRef(false)
    const [position, setPosition] = React.useState({ top: 0, left: 0 })
    const [targetRect, setTargetRect] = React.useState<TourTargetRectState | null>(null)
    const [viewportSize, setViewportSize] = React.useState(() => ({
        width: window.innerWidth,
        height: window.innerHeight,
    }))

    const syncToTarget = React.useCallback(() => {
        const { selectorId, position: positionPref } = step

        const tooltip = tooltipRef.current
        const cardWidth = tooltip?.offsetWidth || TOUR_TOOLTIP_DEFAULT_WIDTH
        const cardHeight = tooltip?.offsetHeight || TOUR_TOOLTIP_DEFAULT_HEIGHT
        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight

        setViewportSize((prev) =>
            prev.width === viewportWidth && prev.height === viewportHeight
                ? prev
                : { width: viewportWidth, height: viewportHeight },
        )

        const target = selectorId.trim() ? findTourTargetElement(selectorId) : null
        if (!target || !isTourStepTargetReady(step, target)) {
            if (!hasLockedTargetRef.current) {
                setTargetRect((prev) => (prev === null ? prev : null))
                const centered = {
                    top: viewportHeight / 2 - cardHeight / 2,
                    left: viewportWidth / 2 - cardWidth / 2,
                }
                setPosition((prev) => (tourPositionsEqual(prev, centered) ? prev : centered))
            }
            return
        }

        hasLockedTargetRef.current = true

        if (!hasScrolledRef.current) {
            scrollTourTargetIntoView(target)
            hasScrolledRef.current = true
        }

        const rect = target.getBoundingClientRect()
        const nextTarget = rectToTargetState(rect)
        setTargetRect((prev) => (tourRectsEqual(prev, nextTarget) ? prev : nextTarget))

        const nextPosition = computeTourTooltipPosition(
            rect,
            positionPref,
            cardWidth,
            cardHeight,
            viewportWidth,
            viewportHeight,
        )
        setPosition((prev) => (tourPositionsEqual(prev, nextPosition) ? prev : nextPosition))
    }, [step])

    React.useLayoutEffect(() => {
        step.onStepEnter?.()
        hasLockedTargetRef.current = false
        hasScrolledRef.current = false

        let cancelled = false
        let pollTimer: number | undefined
        let enterRaf: number | undefined
        let syncRaf: number | undefined
        const pollStartedAt = Date.now()
        const pollTimeoutMs = step.targetPollTimeoutMs ?? 2500
        const pollIntervalMs = 50

        const attemptSync = () => {
            if (cancelled) return
            syncToTarget()
            const needsTarget = step.selectorId.trim().length > 0
            if (!needsTarget) return

            const target = findTourTargetElement(step.selectorId)
            const hasVisibleTarget = Boolean(target && isTourStepTargetReady(step, target))
            if (!hasVisibleTarget && Date.now() - pollStartedAt < pollTimeoutMs) {
                pollTimer = window.setTimeout(attemptSync, pollIntervalMs)
            }
        }

        const startPolling = () => {
            if (cancelled) return
            attemptSync()
        }

        const scheduleSync = () => {
            if (cancelled || syncRaf !== undefined) return
            syncRaf = requestAnimationFrame(() => {
                syncRaf = undefined
                syncToTarget()
            })
        }

        // Defer the first sync so onStepEnter tab switches / drawer opens can render.
        enterRaf = requestAnimationFrame(() => {
            enterRaf = requestAnimationFrame(startPolling)
        })

        window.addEventListener("resize", scheduleSync)
        document.addEventListener("scroll", scheduleSync, true)

        const resizeObserver =
            typeof window.ResizeObserver !== "undefined" && tooltipRef.current
                ? new window.ResizeObserver(scheduleSync)
                : null
        if (tooltipRef.current && resizeObserver) {
            resizeObserver.observe(tooltipRef.current)
        }

        return () => {
            cancelled = true
            if (pollTimer !== undefined) window.clearTimeout(pollTimer)
            if (enterRaf !== undefined) cancelAnimationFrame(enterRaf)
            if (syncRaf !== undefined) cancelAnimationFrame(syncRaf)
            window.removeEventListener("resize", scheduleSync)
            document.removeEventListener("scroll", scheduleSync, true)
            resizeObserver?.disconnect()
        }
    }, [step, syncToTarget, stepIndex])

    const spotlightRect = targetRect
        ? expandTourSpotlightRect({
              top: targetRect.top,
              left: targetRect.left,
              width: targetRect.width,
              height: targetRect.height,
          })
        : null

    const spotlightMaskImage = React.useMemo(
        () =>
            spotlightRect
                ? buildTourSpotlightMaskImage(
                      viewportSize.width,
                      viewportSize.height,
                      spotlightRect,
                  )
                : null,
        [spotlightRect, viewportSize.height, viewportSize.width],
    )

    const overlayMaskStyle = spotlightMaskImage
        ? {
              WebkitMaskImage: spotlightMaskImage,
              maskImage: spotlightMaskImage,
              WebkitMaskSize: "100% 100%",
              maskSize: "100% 100%",
              WebkitMaskRepeat: "no-repeat",
              maskRepeat: "no-repeat",
          }
        : undefined

    return createPortal(
        <>
            <div
                className="fixed inset-0 z-[998] cursor-pointer bg-black/40"
                style={overlayMaskStyle}
                onClick={onClose}
                aria-hidden="true"
            />
            {/* Tooltip card */}
            <div
                ref={tooltipRef}
                className="pointer-events-auto fixed z-[1000] w-80 max-w-[calc(100vw-1rem)]"
                style={{ top: position.top, left: position.left }}
            >
                <Card className="flex max-h-[min(70vh,calc(100vh-1rem))] flex-col shadow-xl border-primary/20">
                    <CardHeader className="pb-2 pr-10">
                        <button
                            onClick={onClose}
                            className="absolute top-3 right-3 rounded-sm opacity-70 hover:opacity-100 transition-opacity"
                            aria-label="Close tour"
                        >
                            <XIcon className="h-4 w-4" />
                        </button>
                        <div className="text-xs text-muted-foreground font-medium">
                            Step {stepIndex + 1} of {totalSteps}
                        </div>
                    </CardHeader>
                    <CardContent className="min-h-0 flex-1 overflow-y-auto pt-0">
                        {step.content}
                    </CardContent>
                    <CardFooter className={cn("shrink-0 pt-2 gap-2", stepIndex === 0 ? "justify-end" : "justify-between")}>
                        {stepIndex > 0 && (
                            <Button variant="outline" size="sm" onClick={onPrevious}>
                                <ChevronLeft className="h-4 w-4 mr-1" />
                                Back
                            </Button>
                        )}
                        <Button variant="default" size="sm" onClick={onNext}>
                            {stepIndex === totalSteps - 1 ? "Finish" : "Next"}
                            {stepIndex < totalSteps - 1 && <ChevronRight className="h-4 w-4 ml-1" />}
                        </Button>
                    </CardFooter>
                </Card>
            </div>
        </>,
        document.body
    )
}

interface TourAlertDialogProps {
    isOpen: boolean
    setIsOpen: (open: boolean) => void
    title?: string
    description?: string
    confirmLabel?: string
    cancelLabel?: string
    icon?: LucideIcon
    /** When set, runs instead of `start()` (e.g. page tours that load steps via `startWithSteps`). */
    onConfirm?: () => void
    /** When set, runs instead of `close()` on skip (e.g. welcome prompt before any tour session exists). */
    onSkip?: () => void
}

const tourWelcomeConfettiPieces: Array<{
    tx: string
    ty: string
    rot: string
    delayMs: number
    className: string
}> = [
    { tx: "-3.25rem", ty: "-0.5rem", rot: "-160deg", delayMs: 0, className: "bg-primary-400 dark:bg-primary-500" },
    { tx: "3rem", ty: "-0.75rem", rot: "145deg", delayMs: 45, className: "bg-tennis-400 dark:bg-tennis-500" },
    { tx: "-1.75rem", ty: "-1.5rem", rot: "-95deg", delayMs: 90, className: "bg-amber-400 dark:bg-amber-500" },
    { tx: "2rem", ty: "-1.35rem", rot: "105deg", delayMs: 120, className: "bg-sky-400 dark:bg-sky-500" },
    { tx: "-0.25rem", ty: "-2rem", rot: "25deg", delayMs: 60, className: "bg-primary-300 dark:bg-primary-400" },
    { tx: "0.5rem", ty: "-1.85rem", rot: "-35deg", delayMs: 100, className: "bg-tennis-300 dark:bg-tennis-400" },
    { tx: "-2.75rem", ty: "0.25rem", rot: "200deg", delayMs: 30, className: "bg-violet-400 dark:bg-violet-500" },
    { tx: "2.85rem", ty: "0.15rem", rot: "-210deg", delayMs: 75, className: "bg-emerald-400 dark:bg-emerald-500" },
]

export function TourAlertDialog({
    isOpen,
    setIsOpen,
    title = "Welcome to CourtSync!",
    description = "Discover how to get the most out of your academy dashboard. We'll show you the essentials in just a few steps.",
    confirmLabel = "Start Tour",
    cancelLabel = "Skip",
    icon: Icon = PartyPopper,
    onConfirm,
    onSkip,
}: TourAlertDialogProps) {
    const { start, close } = useTour()

    if (!isOpen) return null

    const handleSkip = () => {
        if (onSkip) {
            onSkip()
        } else {
            // Same as finishing/closing the overlay: persist via TourProvider onComplete.
            close()
        }
        setIsOpen(false)
    }

    return createPortal(
        <div
            className="tour-welcome-backdrop fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="tour-welcome-title"
            aria-describedby="tour-welcome-description"
        >
            <div
                className="fixed inset-0 bg-black/50"
                onClick={handleSkip}
                aria-hidden="true"
            />
            <div className="relative w-full max-w-lg">
                <div
                    className="pointer-events-none absolute left-1/2 top-8 z-0 flex h-24 w-48 -translate-x-1/2 items-start justify-center"
                    aria-hidden
                >
                    {tourWelcomeConfettiPieces.map((piece, i) => (
                        <span
                            key={i}
                            className={`tour-welcome-confetti-piece absolute top-1/2 h-2.5 w-2.5 rounded-sm shadow-sm ${piece.className}`}
                            style={
                                {
                                    "--tx": piece.tx,
                                    "--ty": piece.ty,
                                    "--rot": piece.rot,
                                    animationDelay: `${piece.delayMs}ms`,
                                } as React.CSSProperties
                            }
                        />
                    ))}
                </div>
                <Card className="tour-welcome-modal relative mx-auto w-full overflow-hidden border-primary/20 shadow-2xl">
                    <div
                        className="h-1.5 w-full bg-gradient-to-r from-primary-500 via-tennis-400 to-primary-500"
                        aria-hidden
                    />
                    <CardHeader className="relative space-y-4 pb-6 pt-8 text-center sm:px-10 sm:pt-10">
                        <div className="flex justify-center" aria-hidden>
                            <Icon
                                className="tour-welcome-icon h-16 w-16 text-tennis-500 dark:text-tennis-400"
                                strokeWidth={1.5}
                            />
                        </div>
                        <div className="space-y-2">
                            <CardTitle
                                id="tour-welcome-title"
                                className="font-display text-balance text-lg font-semibold leading-snug sm:text-xl"
                            >
                                {title}
                            </CardTitle>
                            <p
                                id="tour-welcome-description"
                                className="text-balance text-sm text-muted-foreground sm:text-[0.9375rem]"
                            >
                                {description}
                            </p>
                        </div>
                        <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
                            <Button variant="outline" className="w-full sm:w-auto" onClick={handleSkip}>
                                {cancelLabel}
                            </Button>
                            <Button
                                variant="default"
                                className="w-full sm:w-auto"
                                onClick={() => {
                                    setIsOpen(false)
                                    if (onConfirm) {
                                        onConfirm()
                                    } else {
                                        start()
                                    }
                                }}
                            >
                                {confirmLabel}
                            </Button>
                        </div>
                    </CardHeader>
                </Card>
            </div>
        </div>,
        document.body
    )
}
